import { useState } from 'react';
import { Star, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config/api';

export function TrackReviews({ trackId, trackTitle }) {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/${trackId}`);
      setReviews(data);
      setLoaded(true);
    } catch { setLoaded(true); }
  };

  if (!loaded) loadReviews();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim()) { toast.error('Name and comment are required'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, { track_id: trackId, name: name.trim(), rating, comment: comment.trim() });
      setReviews(prev => [data, ...prev]);
      setName(''); setComment(''); setRating(5);
      toast.success('Review posted!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post review');
    }
    setSubmitting(false);
  };

  const StarRating = ({ value, onChange, hover, onHover }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" onClick={() => onChange?.(i)}
          onMouseEnter={() => onHover?.(i)} onMouseLeave={() => onHover?.(0)}
          className="focus:outline-none" data-testid={`star-${i}`}>
          <Star className={`w-5 h-5 transition-colors ${i <= (hover || value) ? 'text-[#DAA520] fill-[#DAA520]' : 'text-[#333]'}`} />
        </button>
      ))}
    </div>
  );

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-4" data-testid={`reviews-${trackId}`}>
      <form onSubmit={handleSubmit} className="bg-[#141414] border border-[#262626] p-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-wider text-[#DAA520]">Leave a Review</p>
        <div className="flex items-center gap-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="bg-[#0A0A0A] border-[#262626] text-sm flex-1" data-testid="review-name" />
          <StarRating value={rating} onChange={setRating} hover={hoverRating} onHover={setHoverRating} />
        </div>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={`What do you think about "${trackTitle}"?`}
          className="bg-[#0A0A0A] border-[#262626] text-sm" rows={2} data-testid="review-comment" />
        <Button type="submit" disabled={submitting} className="bg-[#DAA520] hover:bg-[#FFD700] text-black font-bold text-xs h-9 px-4" data-testid="review-submit">
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> POST REVIEW</>}
        </Button>
      </form>
      {reviews.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-[#A3A3A3]">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
          {reviews.map(r => (
            <div key={r.id} className="bg-[#0A0A0A] border border-[#262626] p-3" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold">{r.name}</span>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} className={`w-3 h-3 ${i <= r.rating ? 'text-[#DAA520] fill-[#DAA520]' : 'text-[#333]'}`} />
                  ))}
                </div>
                <span className="text-[10px] text-[#666] ml-auto">{timeAgo(r.created_at)}</span>
              </div>
              <p className="text-sm text-[#A3A3A3]">{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
